import { useState } from "react";
import { trpc } from "../lib/trpc";
import { loginAs, stopImpersonate, signOut } from "../lib/authActions";
import Avatar from "./Avatar";
import { LogOut, UserCog, Search, ChevronDown, ChevronUp, ArrowLeftRight } from "lucide-react";

export default function UserCard() {
  const { data: streak } = trpc.streak.mine.useQuery();
  const { data: me } = trpc.auth.me.useQuery();
  const [showSwitch, setShowSwitch] = useState(false);
  const [q, setQ] = useState("");

  if (!me?.authenticated) return null;
  const canImpersonate = (me.realRole === "admin" || me.realRole === "master_admin") && !me.impersonating;

  return (
    <div className="hidden w-60 shrink-0 flex-col border-l border-white/5 bg-ink-800/30 p-4 lg:flex">
      <div className="flex items-center gap-3">
        <Avatar name={me.name} src={(me as any).avatarUrl} className="h-10 w-10 rounded-full" />
        <div className="leading-tight">
          <div className="text-sm font-semibold">{me.name}</div>
          <div className="text-xs text-zinc-400">{me.role.replace("_", " ")}</div>
        </div>
      </div>

      {/* Impersonation indicator + exit */}
      {me.impersonating && (
        <button
          onClick={() => stopImpersonate()}
          className="mt-3 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-left text-xs text-amber-200 hover:bg-amber-500/20"
        >
          <ArrowLeftRight size={13} />
          <span className="flex-1">
            Viewing as <span className="font-semibold">{me.name}</span> — back to {me.realName}
          </span>
        </button>
      )}

      {streak && streak.currentStreak !== undefined && (
        <div className="mt-4 rounded-xl border border-white/5 bg-ink-700/40 p-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="flame text-lg">🔥</span>
            <span className="font-semibold text-amber-300">{streak.currentStreak}-day streak</span>
          </div>
          <div className="mt-1 text-xs text-zinc-500">
            Best: {streak.longestStreak} · Freezes: {streak.freezesLeft ?? 0}
          </div>
        </div>
      )}

      <div className="mt-auto space-y-1 pt-4">
        {canImpersonate && (
          <>
            <button
              onClick={() => setShowSwitch((s) => !s)}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-zinc-300 hover:bg-white/5"
            >
              <UserCog size={15} />
              <span className="flex-1 text-left">Login As…</span>
              {showSwitch ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showSwitch && <UserSwitcher q={q} setQ={setQ} />}
          </>
        )}
        <button
          onClick={() => signOut()}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-zinc-300 hover:bg-white/5"
        >
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </div>
  );
}

// Searchable list of users an admin can impersonate. Each row navigates to the
// server impersonation route, which sets the cookie and reloads as that user.
function UserSwitcher({ q, setQ }: { q: string; setQ: (v: string) => void }) {
  const { data: users, isLoading } = trpc.auth.listUsers.useQuery();
  const filtered = (users ?? []).filter(
    (u: any) =>
      !q ||
      u.name.toLowerCase().includes(q.toLowerCase()) ||
      (u.email ?? "").toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <div className="rounded-lg border border-white/10 bg-ink-900/60 p-2">
      <div className="relative mb-2">
        <Search size={13} className="absolute left-2.5 top-2 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search users…"
          className="w-full rounded-md border border-white/10 bg-ink-700/40 py-1.5 pl-7 pr-2 text-xs focus:border-amber-500/50 focus:outline-none"
        />
      </div>
      <div className="max-h-64 space-y-0.5 overflow-y-auto">
        {isLoading && <div className="px-2 py-3 text-center text-xs text-zinc-500">Loading…</div>}
        {filtered.map((u: any) => (
          <button
            key={u.id}
            onClick={() => loginAs(u.id)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-white/5"
          >
            <Avatar
              name={u.name}
              src={u.avatarUrl}
              className="h-6 w-6 rounded-full text-[10px]"
              fallbackClassName="bg-white/10 text-zinc-200"
            />
            <span className="min-w-0">
              <span className="block truncate text-xs font-medium text-zinc-100">{u.name}</span>
              <span className="block truncate text-[10px] text-zinc-500">{u.email}</span>
            </span>
          </button>
        ))}
        {!isLoading && filtered.length === 0 && (
          <div className="px-2 py-3 text-center text-xs text-zinc-500">No matching users.</div>
        )}
      </div>
    </div>
  );
}
